import { Injectable, NgZone, Renderer2, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ScriptService } from '@zero/shared/util-services';
import { BehaviorSubject, Observable, combineLatest, from, switchMap } from 'rxjs';
import {
  SourcePointConfig,
  SourcePointEnvironmentConfig,
  SourcePointPingResult,
  TcfApiPingCallback,
  TcfApiRejectAllCallback,
  TcfApiVendorConsentsCallback,
  VendorGrants,
} from './source-point.entity';
import { SOURCE_POINT_VENDOR } from './vendors.enum';

declare global {
  interface Window {
    _sp_: {
      executeMessaging: () => void;
      destroyMessages: () => void;
      loadPrivacyManagerModal: (privacyManagerId: string) => void;
      config: SourcePointConfig;
    };
    __tcfapi: {
      (event: 'ping', priority: number, callback: TcfApiPingCallback): void;
      (event: 'getCustomVendorConsents', priority: number, callback: TcfApiVendorConsentsCallback): void;
      (event: 'postRejectAll', priority: number, callback: TcfApiRejectAllCallback): void;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class SourcePointService {
  readonly #scriptService = inject(ScriptService);
  readonly #zone = inject(NgZone);
  readonly #translateService = inject(TranslocoService);

  #config: SourcePointEnvironmentConfig;
  #subscribedVendors = new Map<string, BehaviorSubject<boolean>>([]);
  #subscribedPurpose = new Map<string, BehaviorSubject<boolean>>([]);

  public async displayConsentMessage() {
    this.#checkAcceptedConsents();
    this.#zone.runOutsideAngular(() => window._sp_.executeMessaging());
  }

  public displayPrivacyManagerModal() {
    const privacyManagerId = this.#config.privacyManagerId;
    this.#zone.runOutsideAngular(() => window._sp_.loadPrivacyManagerModal(privacyManagerId));
  }

  public destroyConsentMessages() {
    this.#zone.runOutsideAngular(() => window._sp_.destroyMessages());
  }

  public revokeTracking() {
    this.#zone.runOutsideAngular(() =>
      window.__tcfapi('postRejectAll', 2, (_, success) => {
        if (success) {
          window.alert(this.#translateService.translate('source-point.revoke-hint'));
          window.setTimeout(function () {
            window.location.reload();
          }, 3500);
        }
      }),
    );
  }

  public init(renderer: Renderer2, config: SourcePointEnvironmentConfig): Promise<HTMLScriptElement> {
    this.#config = config;
    return this.#zone.runOutsideAngular(() => {
      this.#initSourcePointGlobal();
      return this.#scriptService.injectScript('https://cdn.privacy-mgmt.com/wrapperMessagingWithoutDetection.js', renderer);
    });
  }

  public hasAccepted(vendor: SOURCE_POINT_VENDOR): Observable<boolean> {
    const alreadySubscribed = this.#subscribedVendors.has(vendor);
    if (!alreadySubscribed) {
      this.#subscribedVendors.set(vendor, new BehaviorSubject(false));
    }
    return from(this.#checkAcceptedConsents()).pipe(switchMap(() => this.#subscribedVendors.get(vendor).asObservable()));
  }

  public hasAcceptedPurposes(purposes: string[]): Observable<Record<string, boolean>> {
    return combineLatest({
      ...purposes.reduce((acc, purpose) => ({ ...acc, [purpose]: this.hasPurposeAccepted(purpose) }), {}),
    });
  }

  public hasPurposeAccepted(purpose: string): Observable<boolean> {
    const alreadySubscribed = this.#subscribedPurpose.has(purpose);
    if (!alreadySubscribed) {
      this.#subscribedPurpose.set(purpose, new BehaviorSubject(false));
    }
    return from(this.#checkAcceptedConsents()).pipe(switchMap(() => this.#subscribedPurpose.get(purpose).asObservable()));
  }

  public unsubscribe(vendor: SOURCE_POINT_VENDOR) {
    this.#subscribedVendors.delete(vendor);
  }

  public sourcePointDisplayed(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.#zone.runOutsideAngular(() => {
        const intervalID = setInterval(() => {
          void (async () => {
            const { cmpLoaded, displayStatus } = await this.#ping();
            if (cmpLoaded && (displayStatus === 'hidden' || displayStatus === 'disabled')) {
              await this.#checkAcceptedConsents();
              clearInterval(intervalID);
              this.#zone.run(() => resolve());
            }
          })();
        }, 250);
      });
    });
  }

  #initSourcePointGlobal() {
    window._sp_.config = {
      isSPA: true,
      accountId: this.#config.accountId,
      baseEndpoint: 'https://cdn.privacy-mgmt.com',
      propertyHref: this.#config.propertyHref,
      joinHref: true,
      events: {
        onMessageChoiceSelect: () => this.#handleConsentClick(),
        onPrivacyManagerAction: () => this.#handleConsentClick(),
      },
    };
  }

  async #handleConsentClick() {
    await this.sourcePointDisplayed();
    await this.#checkAcceptedConsents();
  }

  #checkAcceptedConsents = () =>
    new Promise<void>((resolve) => {
      this.#zone.runOutsideAngular(() =>
        window.__tcfapi('getCustomVendorConsents', 2, (gvl: { grants: VendorGrants }, success: boolean) => {
          this.#setGrantsInZone(gvl, success);
          resolve();
        }),
      );
    });

  #setGrantsInZone(gvl: { grants: VendorGrants }, success: boolean) {
    this.#zone.run(() => success && this.#setGrants(gvl));
  }

  #setGrants(gvl: { grants: VendorGrants }) {
    const { grants } = gvl;
    this.#subscribedPurpose.forEach((value, key) => {
      for (const vendor in grants) {
        const grantValue = grants[vendor].purposeGrants[key];
        if (grantValue !== undefined) {
          value.next(grantValue === true);
        }
      }
    });
    this.#subscribedVendors.forEach((value, key) => value.next(grants[key]?.vendorGrant));
  }

  #ping = (): Promise<SourcePointPingResult> =>
    new Promise<SourcePointPingResult>((resolve) =>
      this.#zone.runOutsideAngular(() => window.__tcfapi('ping', 2, (pingReturn) => resolve(pingReturn))),
    );
}
